import { Button, Card, CardContent, CardHeader, CardTitle } from '@suscri/ui';
import {
  Coins,
  Check,
  Sparkles,
  History,
  Package,
  Receipt,
  ExternalLink,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/ui/PageHeader';
import { useAuthenticatedQuery } from '../hooks/useAuthenticatedQuery';
import { dateLocale } from '../lib/i18n';
import { trpc } from '../lib/trpc';

const ENTRY_TYPE_LABELS: Record<string, string> = {
  INITIAL_GRANT: 'Créditos de bienvenida',
  PLAN_GRANT: 'Créditos del plan',
  PACK_PURCHASE: 'Compra de pack',
  PROMO_GRANT: 'Promoción',
  CONSUME: 'Consumo',
  REFUND: 'Devolución',
};

type PendingAction =
  | {
      kind: 'plan';
      code: string;
      name: string;
      price: number;
      founder: boolean;
    }
  | { kind: 'pack'; code: string; name: string; credits: number; price: number }
  | null;

export function PlanPage() {
  const { t } = useTranslation();
  const { enabled } = useAuthenticatedQuery();
  const utils = trpc.useUtils();
  const [pending, setPending] = useState<PendingAction>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data: me } = trpc.billing.me.useQuery(undefined, { enabled });
  const { data: plansData } = trpc.billing.plans.useQuery();
  const { data: packs } = trpc.billing.packs.useQuery();
  const {
    data: history,
    fetchNextPage,
    hasNextPage,
  } = trpc.billing.creditHistory.useInfiniteQuery(
    { limit: 15 },
    { enabled, getNextPageParam: last => last.nextCursor }
  );

  const subscribe = trpc.billing.subscribe.useMutation();
  const purchasePack = trpc.billing.purchasePack.useMutation();
  const { data: invoicesData } = trpc.billing.invoices.useQuery(undefined, {
    enabled,
  });
  const portalSession = trpc.billing.portalSession.useMutation();

  const refresh = () => {
    utils.billing.me.invalidate();
    utils.billing.creditHistory.invalidate();
    utils.billing.plans.invalidate();
  };

  // Vuelta desde Stripe Checkout (?checkout=success|cancelled).
  // El alta/abono real lo hace el webhook, que puede tardar unos segundos:
  // se refresca varias veces para que el usuario vea el cambio sin recargar.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (!checkout) return;
    window.history.replaceState({}, '', '/plan');
    if (checkout === 'success') {
      setFeedback(t('Pago completado. Aplicando los cambios a tu cuenta…'));
      const timers = [2000, 5000, 10000].map(ms => setTimeout(refresh, ms));
      return () => timers.forEach(clearTimeout);
    }
    if (checkout === 'cancelled') {
      setFeedback(t('Pago cancelado. No se ha realizado ningún cargo.'));
    }
  }, []);

  const confirmPending = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'plan') {
        const result = await subscribe.mutateAsync({
          planCode: pending.code as 'FREE' | 'STARTER' | 'PRO' | 'BUSINESS',
          billingCycle: 'MONTHLY',
          founder: pending.founder,
        });
        if (result.checkoutUrl) {
          // Stripe activo: el pago se completa en la página de Stripe
          window.location.href = result.checkoutUrl;
          return;
        }
        setFeedback(
          t('Plan {{name}} activado', { name: pending.name }) +
            (result.founderApplied ? t(' con precio fundador') : '') +
            '.'
        );
      } else {
        const result = await purchasePack.mutateAsync({
          packCode: pending.code,
        });
        if (result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
          return;
        }
        setFeedback(
          t('Pack añadido: +{{credits}} créditos. Saldo actual: {{balance}}.', {
            credits: result.credits,
            balance: result.balance,
          })
        );
      }
      refresh();
    } catch (error) {
      setFeedback(
        error instanceof Error
          ? error.message
          : t('No se pudo completar la operación.')
      );
    } finally {
      setPending(null);
    }
  };

  const currentPlanCode = me?.plan?.code ?? 'FREE';
  const historyEntries = history?.pages.flatMap(p => p.entries) ?? [];

  return (
    <div className="space-y-8">
      <PageHeader eyebrow={t('Facturación')} title={t('Mi Plan')} description={t('Gestiona tu plan, tus créditos y consulta tu historial de consumo')} />

      {feedback && (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 px-4 py-3 text-sm text-blue-800 dark:text-blue-200 flex items-center justify-between">
          <span>{feedback}</span>
          <button
            className="text-blue-500 hover:text-blue-700 font-semibold"
            onClick={() => setFeedback(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Plan actual + saldo */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">
              {t('Plan actual')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {me?.plan?.name ?? 'Free'}
              </span>
              {me?.plan?.isFounderPrice && (
                <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 dark:bg-violet-950 px-2.5 py-0.5 text-xs font-semibold text-violet-700 dark:text-violet-300">
                  <Sparkles className="h-3 w-3" /> {t('Precio fundador')}
                </span>
              )}
            </div>
            {me?.plan && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('{{count}} créditos/mes', { count: me.plan.monthlyCredits })}{' '}
                · {t('Renovación:')}{' '}
                {new Date(me.plan.currentPeriodEnd).toLocaleDateString(
                  dateLocale()
                )}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">
              {t('Créditos disponibles')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Coins className="h-8 w-8 text-amber-500" />
              <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {me?.credits ?? 0}
              </span>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t(
                'Las acciones básicas no consumen créditos; solo las funciones premium.'
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Planes */}
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('Planes')}
        </h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {plansData?.plans.map(plan => {
            const isCurrent = plan.code === currentPlanCode;
            const founderApplies =
              plan.founderPriceMonthly != null && plansData.founderAvailable;
            const price =
              founderApplies && plan.founderPriceMonthly != null
                ? plan.founderPriceMonthly
                : plan.priceMonthly;
            return (
              <Card
                key={plan.code}
                className={isCurrent ? 'ring-2 ring-blue-500' : ''}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    {isCurrent && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-950 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
                        <Check className="h-3 w-3" /> {t('Actual')}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-1">
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {price === 0
                        ? t('Gratis')
                        : `${price.toFixed(2).replace('.', ',')} €`}
                    </span>
                    {price > 0 && (
                      <span className="text-sm text-gray-500">{t('/mes')}</span>
                    )}
                  </div>
                  {founderApplies && (
                    <p className="text-xs text-violet-600 dark:text-violet-400 font-semibold mb-1">
                      <Sparkles className="inline h-3 w-3 mr-0.5" />
                      {t('Precio fundador (antes')}{' '}
                      {plan.priceMonthly.toFixed(2).replace('.', ',')} €)
                    </p>
                  )}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t('{{count}} créditos/mes', {
                      count: plan.monthlyCredits,
                    })}
                    {plan.initialCredits > 0
                      ? ` · ${t('{{count}} de bienvenida', { count: plan.initialCredits })}`
                      : ''}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mb-4 min-h-[3rem]">
                    {plan.description}
                  </p>
                  <Button
                    className="w-full"
                    variant={isCurrent ? 'outline' : 'default'}
                    disabled={isCurrent}
                    onClick={() =>
                      setPending({
                        kind: 'plan',
                        code: plan.code,
                        name: plan.name,
                        price,
                        founder: founderApplies,
                      })
                    }
                  >
                    {isCurrent ? t('Plan actual') : t('Cambiar a este plan')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Packs de créditos */}
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
          <Package className="h-5 w-5" /> {t('Packs de créditos extra')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t(
            'Para picos de uso puntuales, sin cambiar de plan. Los créditos comprados tienen caducidad amplia.'
          )}
        </p>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {packs?.map(pack => (
            <Card key={pack.code}>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {pack.credits.toLocaleString('es-ES')}{' '}
                  <span className="text-sm font-normal text-gray-500">
                    {t('créditos')}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {pack.price.toFixed(2).replace('.', ',')} €
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  {t('Validez: {{count}} meses', {
                    count: pack.validityMonths,
                  })}
                </p>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() =>
                    setPending({
                      kind: 'pack',
                      code: pack.code,
                      name: pack.name,
                      credits: pack.credits,
                      price: pack.price,
                    })
                  }
                >
                  {t('Comprar')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Facturas (M7): solo aparece cuando hay pagos reales vía Stripe */}
      {invoicesData?.invoices && invoicesData.invoices.length > 0 && (
        <section>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
              <Receipt className="h-5 w-5" /> {t('Facturas')}
            </h2>
            <Button
              variant="outline"
              disabled={portalSession.isPending}
              onClick={async () => {
                try {
                  const { url } = await portalSession.mutateAsync();
                  window.location.href = url;
                } catch (error) {
                  setFeedback(
                    error instanceof Error
                      ? error.message
                      : t('No se pudo abrir el portal de facturación.')
                  );
                }
              }}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {portalSession.isPending
                ? t('Abriendo…')
                : t('Gestionar facturación en Stripe')}
            </Button>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {invoicesData.invoices.map(inv => (
                  <div
                    key={inv.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {inv.number ?? inv.id}
                        <span
                          className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${
                            inv.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {inv.status === 'paid'
                            ? t('pagada')
                            : (inv.status ?? t('pendiente'))}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(inv.created).toLocaleDateString(dateLocale())}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {inv.total.toFixed(2).replace('.', ',')} {inv.currency}
                      </span>
                      {inv.invoicePdf && (
                        <a
                          href={inv.invoicePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          PDF
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Historial de créditos */}
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <History className="h-5 w-5" /> {t('Historial de créditos')}
        </h2>
        <Card>
          <CardContent className="pt-6">
            {historyEntries.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t('Todavía no hay movimientos de créditos.')}
              </p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {historyEntries.map(entry => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {entry.description ??
                          t(ENTRY_TYPE_LABELS[entry.type] ?? entry.type)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(entry.createdAt).toLocaleString(dateLocale())}
                        {entry.type === 'PROMO_GRANT' &&
                          ` · ${t('Promocional')}`}
                        {entry.expiresAt &&
                          entry.amount > 0 &&
                          ` · ${t('Caduca:')} ${new Date(entry.expiresAt).toLocaleDateString(dateLocale())}`}
                      </p>
                    </div>
                    <span
                      className={`text-sm font-bold ${
                        entry.amount >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {entry.amount >= 0 ? `+${entry.amount}` : entry.amount}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {hasNextPage && (
              <div className="mt-4 text-center">
                <Button variant="outline" onClick={() => fetchNextPage()}>
                  {t('Cargar más')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Modal de confirmación */}
      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPending(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {pending.kind === 'plan'
                ? t('Cambiar al plan {{name}}', { name: pending.name })
                : t('Comprar {{name}}', { name: pending.name })}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {pending.kind === 'plan'
                ? pending.price === 0
                  ? t(
                      'Tu cuenta pasará al plan gratuito. Conservas los créditos que ya tienes.'
                    )
                  : t('El plan {{name}} cuesta {{price}} €/mes', {
                      name: pending.name,
                      price: pending.price.toFixed(2).replace('.', ','),
                    }) +
                    (pending.founder
                      ? t(
                          ' (precio fundador, lo conservas mientras mantengas la suscripción)'
                        )
                      : '') +
                    '.'
                : t('Recibirás {{credits}} créditos por {{price}} €.', {
                    credits: pending.credits.toLocaleString(dateLocale()),
                    price: pending.price.toFixed(2).replace('.', ','),
                  })}
            </p>
            <p className="text-xs text-gray-400 mb-6">
              {t(
                'Si los pagos están activos, te llevaremos a la pasarela segura de Stripe para completar la compra; si no (beta), la activación es inmediata y sin cargo.'
              )}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPending(null)}>
                {t('Cancelar')}
              </Button>
              <Button
                onClick={confirmPending}
                disabled={subscribe.isPending || purchasePack.isPending}
              >
                {subscribe.isPending || purchasePack.isPending
                  ? t('Procesando...')
                  : t('Confirmar')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
